import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { CompanyAdminLayout } from "../../components/CompanyAdmin/CompanyAdminLayout";
import { TodoKanbanColumn } from "../../components/CompanyAdmin/TodoStatus/TodoKanbanColumn";
import { TodoDetailDialog } from "../../components/CompanyAdmin/TodoStatus/TodoDetailDialog";
import { DependencyWarningDialog } from "../../components/CompanyAdmin/TodoStatus/DependencyWarningDialog";
import { useAuthStore } from "../../stores/auth-store";
import { orderTodoUseCase, companyUseCase } from "../../../di/container";
import type {
  OrderTodo,
  TodoStatus,
  CompanyTodosFilter,
  OrderTodoDependency,
} from "../../../domain/types/todo";
import type { UserInfoResponse } from "../../../shared/types/api";
import {
  User,
  Filter,
  ArrowUpDown,
  Loader2,
  AlertCircle,
  RefreshCw,
  LayoutGrid,
  X as XIcon,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { TodoKanbanCard } from "../../components/CompanyAdmin/TodoStatus/TodoKanbanCard";

type SortOption = "priority" | "dueDate" | "createdAt";

const STATUSES: TodoStatus[] = ["PENDING", "WAITING", "IN_PROGRESS", "COMPLETED"];

// 주문별 그룹 정보
interface OrderGroup {
  orderId: number;
  orderName: string;
  orderNumber: string;
  todos: OrderTodo[];
}

// 개별 칸반 보드 컴포넌트 (주문별로 보기용)
interface OrderKanbanBoardProps {
  orderGroup: OrderGroup;
  isExpanded: boolean;
  onToggle: () => void;
  onTodoClick: (todo: OrderTodo) => void;
  sortTodos: (todos: OrderTodo[]) => OrderTodo[];
  sensors: ReturnType<typeof useSensors>;
  onDragStart: (event: DragStartEvent) => void;
  onDragEnd: (event: DragEndEvent) => void;
  activeTodo: OrderTodo | null;
}

const OrderKanbanBoard: React.FC<OrderKanbanBoardProps> = ({
  orderGroup,
  isExpanded,
  onToggle,
  onTodoClick,
  sortTodos,
  sensors,
  onDragStart,
  onDragEnd,
  activeTodo,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const COLUMN_WIDTH = 250;

  const checkScrollability = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
  }, []);

  const handleScrollLeft = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollBy({ left: -COLUMN_WIDTH, behavior: "smooth" });
  }, []);

  const handleScrollRight = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollBy({ left: COLUMN_WIDTH, behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (!isExpanded) return;

    const container = scrollContainerRef.current;
    if (!container) return;

    checkScrollability();
    container.addEventListener("scroll", checkScrollability);
    window.addEventListener("resize", checkScrollability);

    return () => {
      container.removeEventListener("scroll", checkScrollability);
      window.removeEventListener("resize", checkScrollability);
    };
  }, [checkScrollability, isExpanded, orderGroup.todos]);

  const getTodosByStatus = (status: TodoStatus): OrderTodo[] => {
    const filtered = orderGroup.todos.filter((t) => t.status === status);
    return sortTodos(filtered);
  };

  return (
    <div className="bg-white rounded-[0.625rem] shadow-sm">
      {/* Header with toggle button */}
      <div
        className="flex items-center justify-between p-[18px] cursor-pointer hover:bg-gray-50 transition-colors rounded-t-[0.625rem]"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-gray-900">
            {orderGroup.orderName}
          </h3>
          <span className="text-sm text-gray-500">
            ({orderGroup.orderNumber})
          </span>
          <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full">
            {orderGroup.todos.length}개 작업
          </span>
        </div>
        <button
          className="p-1 rounded hover:bg-gray-100 transition-colors"
          aria-label={isExpanded ? "접기" : "펼치기"}
        >
          {isExpanded ? (
            <ChevronUp size={20} className="text-gray-500" />
          ) : (
            <ChevronDown size={20} className="text-gray-500" />
          )}
        </button>
      </div>

      {/* Kanban Content */}
      <div
        className={`overflow-hidden transition-all duration-300 ${
          isExpanded ? "max-h-[800px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="px-[18px] pb-[18px]">
          <div className="relative">
            {/* 왼쪽 스크롤 버튼 */}
            {canScrollLeft && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleScrollLeft();
                }}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10
                  w-10 h-10 rounded-r-full bg-white/80 shadow-[10px_10px_20px_-3px_rgba(0,0,0,0.1)]
                  flex items-center justify-center
                  hover:bg-white transition-colors"
                aria-label="이전 컬럼으로 이동"
              >
                <svg
                  className="w-5 h-5 text-gray-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
              </button>
            )}

            {/* 오른쪽 스크롤 버튼 */}
            {canScrollRight && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleScrollRight();
                }}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10
                  w-10 h-10 rounded-l-full bg-white/80 shadow-[-10px_10px_20px_-3px_rgba(0,0,0,0.1)]
                  flex items-center justify-center
                  hover:bg-white transition-colors"
                aria-label="다음 컬럼으로 이동"
              >
                <svg
                  className="w-5 h-5 text-gray-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            )}

            <div
              ref={scrollContainerRef}
              className="kanban-scroll-container overflow-x-auto"
            >
              <DndContext
                sensors={sensors}
                collisionDetection={closestCorners}
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
              >
                <div className="flex gap-[0.625rem] justify-start min-w-max px-1 py-1">
                  {STATUSES.map((status) => (
                    <TodoKanbanColumn
                      key={`${orderGroup.orderId}-${status}`}
                      status={status}
                      todos={getTodosByStatus(status)}
                      onTodoClick={onTodoClick}
                    />
                  ))}
                </div>

                <DragOverlay>
                  {activeTodo && (
                    <div className="rotate-3">
                      <TodoKanbanCard
                        todo={activeTodo}
                        onClick={() => {}}
                        isDragging
                      />
                    </div>
                  )}
                </DragOverlay>
              </DndContext>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const TodoStatusManagement: React.FC = () => {
  const { user } = useAuthStore();
  const companyId = user?.companyId;

  // State
  const [todos, setTodos] = useState<OrderTodo[]>([]);
  const [companyUsers, setCompanyUsers] = useState<UserInfoResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [showMyTodosOnly, setShowMyTodosOnly] = useState(false);
  const [selectedAssigneeId, setSelectedAssigneeId] = useState<number | null>(null);
  const [groupByOrder, setGroupByOrder] = useState(false);
  const [sortBy, setSortBy] = useState<SortOption>("priority");

  // Accordion state for group by order view
  const [expandedOrders, setExpandedOrders] = useState<Set<number>>(new Set());

  // Dialog state
  const [selectedTodo, setSelectedTodo] = useState<OrderTodo | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  // Warning dialog state
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<{
    todo: OrderTodo;
    targetStatus: TodoStatus;
    warningDependencies: OrderTodoDependency[];
  } | null>(null);
  const [isStatusChanging, setIsStatusChanging] = useState(false);

  // Drag state
  const [activeTodo, setActiveTodo] = useState<OrderTodo | null>(null);

  // Scroll state (일반 보기용)
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const COLUMN_WIDTH = 250;

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // 주문별 그룹화
  const orderGroups = useMemo((): OrderGroup[] => {
    const groupMap = new Map<number, OrderGroup>();

    todos.forEach((todo) => {
      const orderId = todo.orderId;
      if (!groupMap.has(orderId)) {
        groupMap.set(orderId, {
          orderId,
          orderName: todo.order?.name || `주문 #${orderId}`,
          orderNumber: todo.order?.orderNumber || "",
          todos: [],
        });
      }
      groupMap.get(orderId)!.todos.push(todo);
    });

    return Array.from(groupMap.values());
  }, [todos]);

  // 주문별로 보기 토글 시 모든 주문 펼치기
  useEffect(() => {
    if (groupByOrder && orderGroups.length > 0) {
      setExpandedOrders(new Set(orderGroups.map((g) => g.orderId)));
    }
  }, [groupByOrder, orderGroups.length]);

  const toggleOrderExpanded = (orderId: number) => {
    setExpandedOrders((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
      }
      return newSet;
    });
  };

  // 스크롤 가능 여부 체크
  const checkScrollability = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
  }, []);

  // 스크롤 이동 핸들러
  const handleScrollLeft = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollBy({ left: -COLUMN_WIDTH, behavior: "smooth" });
  }, []);

  const handleScrollRight = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollBy({ left: COLUMN_WIDTH, behavior: "smooth" });
  }, []);

  // 스크롤 이벤트 리스너
  useEffect(() => {
    if (groupByOrder) return;

    const container = scrollContainerRef.current;
    if (!container) return;

    checkScrollability();
    container.addEventListener("scroll", checkScrollability);
    window.addEventListener("resize", checkScrollability);

    return () => {
      container.removeEventListener("scroll", checkScrollability);
      window.removeEventListener("resize", checkScrollability);
    };
  }, [checkScrollability, todos, groupByOrder]);

  // Fetch users
  useEffect(() => {
    const fetchUsers = async () => {
      if (!companyId) return;

      try {
        const usersResponse = await companyUseCase.getUserByCompanyId({
          companyId,
          pageable: { page: 0, size: 100 },
        });
        const managers = (usersResponse.items || []).filter(
          (u: UserInfoResponse) => u.role === "COMPANY_ADMIN" || u.role === "COMPANY_MANAGER"
        );
        setCompanyUsers(managers);
      } catch (err) {
        console.error("Failed to fetch users:", err);
      }
    };

    fetchUsers();
  }, [companyId]);

  // Fetch todos
  const fetchTodos = useCallback(async () => {
    if (!companyId) return;

    setIsLoading(true);
    setError(null);

    try {
      let todoData: OrderTodo[];

      if (showMyTodosOnly) {
        todoData = await orderTodoUseCase.getMyTodos(companyId);
      } else {
        const filter: CompanyTodosFilter = {};
        if (selectedAssigneeId) {
          filter.assigneeUserId = selectedAssigneeId;
        }
        todoData = await orderTodoUseCase.getCompanyTodos(companyId, filter);
      }

      // Ensure todoData is an array
      setTodos(Array.isArray(todoData) ? todoData : []);
    } catch (err) {
      console.error("Failed to fetch todos:", err);
      setError("작업 현황을 불러오는데 실패했습니다. (API가 구현되지 않았을 수 있습니다)");
      setTodos([]); // Reset to empty array on error
    } finally {
      setIsLoading(false);
    }
  }, [companyId, showMyTodosOnly, selectedAssigneeId]);

  useEffect(() => {
    fetchTodos();
  }, [fetchTodos]);

  // Sort todos
  const sortTodos = useCallback((todosToSort: OrderTodo[]): OrderTodo[] => {
    return [...todosToSort].sort((a, b) => {
      switch (sortBy) {
        case "priority": {
          const priorityOrder = { HIGHEST: 5, HIGH: 4, MEDIUM: 3, LOW: 2, LOWEST: 1 };
          const aPriority = a.order?.priority ? priorityOrder[a.order.priority] : 0;
          const bPriority = b.order?.priority ? priorityOrder[b.order.priority] : 0;
          return bPriority - aPriority;
        }
        case "dueDate": {
          const aDate = a.order?.dueDate ? new Date(a.order.dueDate).getTime() : Infinity;
          const bDate = b.order?.dueDate ? new Date(b.order.dueDate).getTime() : Infinity;
          return aDate - bDate;
        }
        case "createdAt":
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        default:
          return 0;
      }
    });
  }, [sortBy]);

  // Group todos by status
  const getTodosByStatus = (status: TodoStatus): OrderTodo[] => {
    const filtered = todos.filter((t) => t.status === status);
    return sortTodos(filtered);
  };

  // Handlers
  const handleTodoClick = (todo: OrderTodo) => {
    setSelectedTodo(todo);
    setIsDetailOpen(true);
  };

  const handleAddNote = async (todoId: number, content: string) => {
    if (!companyId) return;

    const todo = todos.find((t) => t.id === todoId);
    if (!todo) return;

    try {
      const note = await orderTodoUseCase.addNote(companyId, todo.orderId, todoId, {
        content,
      });
      setTodos((prev) =>
        prev.map((t) =>
          t.id === todoId ? { ...t, notes: [...t.notes, note] } : t
        )
      );
      // Update selected todo if it's the same
      if (selectedTodo?.id === todoId) {
        setSelectedTodo((prev) =>
          prev ? { ...prev, notes: [...prev.notes, note] } : prev
        );
      }
    } catch (err) {
      console.error("Failed to add note:", err);
      setError("메모 추가에 실패했습니다.");
    }
  };

  const checkDependencies = (
    todo: OrderTodo,
    targetStatus: TodoStatus
  ): { canProceed: boolean; warningDeps: OrderTodoDependency[] } => {
    // Only check when moving to IN_PROGRESS
    if (targetStatus !== "IN_PROGRESS") {
      return { canProceed: true, warningDeps: [] };
    }

    const blockingDeps = todo.dependencies.filter(
      (d) =>
        d.dependencyType === "BLOCKING" && d.dependsOnTodoStatus !== "COMPLETED"
    );

    if (blockingDeps.length > 0) {
      return { canProceed: false, warningDeps: [] };
    }

    const warningDeps = todo.dependencies.filter(
      (d) =>
        d.dependencyType === "WARNING" && d.dependsOnTodoStatus !== "COMPLETED"
    );

    return { canProceed: true, warningDeps };
  };

  const handleStatusChange = async (todo: OrderTodo, newStatus: TodoStatus) => {
    if (!companyId || todo.status === newStatus) return;

    const { canProceed, warningDeps } = checkDependencies(todo, newStatus);

    if (!canProceed) {
      // 차단 중인 선행 작업 이름들 추출
      const blockingDeps = todo.dependencies.filter(
        (d) => d.dependencyType === "BLOCKING" && d.dependsOnTodoStatus !== "COMPLETED"
      );
      const blockingNames = blockingDeps
        .map((d) => d.dependsOnTodoName || `작업 #${d.dependsOnTodoId}`)
        .join(", ");
      setError(
        `선행 작업이 완료되지 않아 진행할 수 없습니다. (대기 중: ${blockingNames})`
      );
      return;
    }

    if (warningDeps.length > 0) {
      setPendingStatusChange({
        todo,
        targetStatus: newStatus,
        warningDependencies: warningDeps,
      });
      setWarningDialogOpen(true);
      return;
    }

    await executeStatusChange(todo, newStatus);
  };

  const executeStatusChange = async (todo: OrderTodo, newStatus: TodoStatus) => {
    if (!companyId) return;

    setIsStatusChanging(true);
    try {
      const updated = await orderTodoUseCase.updateStatus(
        companyId,
        todo.orderId,
        todo.id,
        { status: newStatus }
      );

      // 상태 변경 후 의존성 동기화:
      // 변경된 todo에 의존하는 다른 todo들의 dependsOnTodoStatus도 업데이트
      setTodos((prev) =>
        prev.map((t) => {
          if (t.id === todo.id) {
            return updated;
          }
          // 이 todo가 변경된 todo에 의존하고 있다면, 의존성 상태 업데이트
          const hasDepOnChangedTodo = t.dependencies.some(
            (d) => d.dependsOnTodoId === todo.id
          );
          if (hasDepOnChangedTodo) {
            return {
              ...t,
              dependencies: t.dependencies.map((d) =>
                d.dependsOnTodoId === todo.id
                  ? { ...d, dependsOnTodoStatus: newStatus }
                  : d
              ),
            };
          }
          return t;
        })
      );

      // Update selected todo if it's the same
      if (selectedTodo?.id === todo.id) {
        setSelectedTodo(updated);
      }

      setWarningDialogOpen(false);
      setPendingStatusChange(null);
    } catch (err) {
      console.error("Failed to update status:", err);
      setError("상태 변경에 실패했습니다.");
    } finally {
      setIsStatusChanging(false);
    }
  };

  const handleWarningConfirm = async () => {
    if (!pendingStatusChange) return;
    await executeStatusChange(
      pendingStatusChange.todo,
      pendingStatusChange.targetStatus
    );
  };

  // Drag handlers
  const handleDragStart = (event: DragStartEvent) => {
    const todo = todos.find((t) => t.id === event.active.id);
    setActiveTodo(todo || null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveTodo(null);

    if (!over) return;

    const todoId = active.id as number;
    const newStatus = over.id as TodoStatus;

    const todo = todos.find((t) => t.id === todoId);
    if (!todo || todo.status === newStatus) return;

    await handleStatusChange(todo, newStatus);
  };

  const eligibleUsers = companyUsers.filter(
    (u) => u.role === "COMPANY_ADMIN" || u.role === "COMPANY_MANAGER"
  );

  return (
    <CompanyAdminLayout>
      <div className="p-5 space-y-6 overflow-x-hidden">
        <style>{`
          .kanban-scroll-container {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .kanban-scroll-container::-webkit-scrollbar {
            display: none;
            width: 0;
            height: 0;
          }
        `}</style>

        {/* Header */}
        <div className="flex flex-col items-start gap-[0.5rem]">
          <h2 className="text-[1.375rem] font-semibold">작업 현황 관리</h2>
        </div>

        {/* Filters Card */}
        <div className="p-[1.25rem] border border-[#d9d9d9] rounded-[0.625rem] bg-white">
          <div className="flex flex-col md:flex-row gap-[0.75rem] items-stretch justify-between">
            <div className="flex flex-wrap items-center gap-[0.75rem]">
              {/* Assignee Filter */}
              {eligibleUsers.length > 0 && (
                <div className={`relative h-[2.5rem] ${showMyTodosOnly ? "opacity-50" : ""}`}>
                  <div className="absolute left-3 top-0 bottom-0 flex justify-center items-center pointer-events-none">
                    <User size={16} className="text-gray-400" />
                  </div>
                  <select
                    value={selectedAssigneeId || ""}
                    onChange={(e) =>
                      setSelectedAssigneeId(
                        e.target.value ? Number(e.target.value) : null
                      )
                    }
                    disabled={showMyTodosOnly}
                    className={`h-full bg-white px-4 py-2 pl-9 pr-8 text-sm rounded-[8px] border border-gray-300
                      appearance-none focus:outline-none focus:border-[#4285F4] text-gray-800 ${
                        showMyTodosOnly ? "cursor-not-allowed bg-gray-50" : "cursor-pointer"
                      }`}
                  >
                    <option value="">전체 담당자</option>
                    {eligibleUsers.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name || user.email}
                      </option>
                    ))}
                  </select>
                  <div className="absolute right-3 top-0 bottom-0 flex justify-center items-center pointer-events-none">
                    <ChevronDown size={14} className="text-gray-400" />
                  </div>
                </div>
              )}

              {/* My Todos Checkbox */}
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={showMyTodosOnly}
                  onChange={(e) => {
                    setShowMyTodosOnly(e.target.checked);
                    if (e.target.checked) {
                      setSelectedAssigneeId(null);
                    }
                  }}
                  className="w-4 h-4 rounded border-gray-300 text-[#4285F4] focus:ring-[#4285F4] cursor-pointer"
                />
                <span className="text-sm text-gray-700">내 작업만 보기</span>
              </label>

              {/* Group by Order Checkbox */}
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={groupByOrder}
                  onChange={(e) => setGroupByOrder(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-[#4285F4] focus:ring-[#4285F4] cursor-pointer"
                />
                <span className="text-sm text-gray-700">주문별로 보기</span>
              </label>
            </div>

            <div className="flex items-center gap-[0.75rem]">
              {/* Sort */}
              <div className="relative h-[2.5rem]">
                <div className="absolute left-3 top-0 bottom-0 flex justify-center items-center pointer-events-none">
                  <ArrowUpDown size={16} className="text-gray-400" />
                </div>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as SortOption)}
                  className="h-full bg-white px-4 py-2 pl-9 pr-8 text-sm rounded-[8px] border border-gray-300
                    appearance-none focus:outline-none focus:border-[#4285F4] text-gray-800 cursor-pointer"
                >
                  <option value="priority">우선순위</option>
                  <option value="dueDate">마감일</option>
                  <option value="createdAt">생성일</option>
                </select>
                <div className="absolute right-3 top-0 bottom-0 flex justify-center items-center pointer-events-none">
                  <ChevronDown size={14} className="text-gray-400" />
                </div>
              </div>

              {/* Refresh Button */}
              <button
                onClick={fetchTodos}
                disabled={isLoading}
                className="h-[2.5rem] px-4 flex items-center gap-2 text-sm rounded-[8px] border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                <RefreshCw size={16} className={isLoading ? "animate-spin" : ""} />
                <span>새로고침</span>
              </button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-[0.625rem] p-3 flex items-center gap-2 text-red-700 text-sm">
            <AlertCircle size={16} />
            <span className="flex-1">{error}</span>
            <button
              onClick={() => setError(null)}
              className="text-red-500 hover:text-red-700"
            >
              <XIcon size={16} />
            </button>
          </div>
        )}

        {/* Loading */}
        {isLoading ? (
          <div className="bg-white rounded-[0.625rem] shadow-sm p-[18px]">
            <div className="flex items-center justify-center py-12 text-gray-500 text-sm">
              <Loader2 className="animate-spin mr-2" size={18} />
              <span>불러오는 중...</span>
            </div>
          </div>
        ) : groupByOrder ? (
          /* 주문별로 보기 - 여러 개의 아코디언 칸반 */
          <div className="space-y-4">
            {orderGroups.length === 0 ? (
              <div className="bg-white rounded-[0.625rem] shadow-sm p-[18px]">
                <div className="flex items-center justify-center py-12 text-gray-500 text-sm">
                  작업이 없습니다.
                </div>
              </div>
            ) : (
              orderGroups.map((orderGroup) => (
                <OrderKanbanBoard
                  key={orderGroup.orderId}
                  orderGroup={orderGroup}
                  isExpanded={expandedOrders.has(orderGroup.orderId)}
                  onToggle={() => toggleOrderExpanded(orderGroup.orderId)}
                  onTodoClick={handleTodoClick}
                  sortTodos={sortTodos}
                  sensors={sensors}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  activeTodo={activeTodo}
                />
              ))
            )}
          </div>
        ) : (
          /* 일반 보기 - 단일 칸반 보드 */
          <div className="bg-white rounded-[0.625rem] shadow-sm p-[18px]">
            <h3 className="text-lg font-semibold text-gray-900 mb-[0.75rem] text-left p-[2px]">
              작업 현황
            </h3>

            <div className="relative">
              {/* 왼쪽 스크롤 버튼 */}
              {canScrollLeft && (
                <button
                  onClick={handleScrollLeft}
                  className="absolute left-0 top-1/2 -translate-y-1/2 z-10
                    w-10 h-10 rounded-r-full bg-white/80 shadow-[10px_10px_20px_-3px_rgba(0,0,0,0.1)]
                    flex items-center justify-center
                    hover:bg-white transition-colors"
                  aria-label="이전 컬럼으로 이동"
                >
                  <svg
                    className="w-5 h-5 text-gray-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
              )}

              {/* 오른쪽 스크롤 버튼 */}
              {canScrollRight && (
                <button
                  onClick={handleScrollRight}
                  className="absolute right-0 top-1/2 -translate-y-1/2 z-10
                    w-10 h-10 rounded-l-full bg-white/80 shadow-[-10px_10px_20px_-3px_rgba(0,0,0,0.1)]
                    flex items-center justify-center
                    hover:bg-white transition-colors"
                  aria-label="다음 컬럼으로 이동"
                >
                  <svg
                    className="w-5 h-5 text-gray-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              )}

              <div
                ref={scrollContainerRef}
                className="kanban-scroll-container overflow-x-auto"
              >
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCorners}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                >
                  <div className="flex gap-[0.625rem] justify-start min-w-max px-1 py-1">
                    {STATUSES.map((status) => (
                      <TodoKanbanColumn
                        key={status}
                        status={status}
                        todos={getTodosByStatus(status)}
                        onTodoClick={handleTodoClick}
                      />
                    ))}
                  </div>

                  <DragOverlay>
                    {activeTodo && (
                      <div className="rotate-3">
                        <TodoKanbanCard
                          todo={activeTodo}
                          onClick={() => {}}
                          isDragging
                        />
                      </div>
                    )}
                  </DragOverlay>
                </DndContext>
              </div>
            </div>
          </div>
        )}

        {/* Todo Detail Dialog */}
        <TodoDetailDialog
          isOpen={isDetailOpen}
          onClose={() => {
            setIsDetailOpen(false);
            setSelectedTodo(null);
          }}
          todo={selectedTodo}
          onAddNote={handleAddNote}
          onStatusChange={handleStatusChange}
          isLoading={isStatusChanging}
        />

        {/* Dependency Warning Dialog */}
        <DependencyWarningDialog
          isOpen={warningDialogOpen}
          onClose={() => {
            setWarningDialogOpen(false);
            setPendingStatusChange(null);
          }}
          onConfirm={handleWarningConfirm}
          todo={pendingStatusChange?.todo || null}
          targetStatus={pendingStatusChange?.targetStatus || null}
          warningDependencies={pendingStatusChange?.warningDependencies || []}
          isLoading={isStatusChanging}
        />
      </div>
    </CompanyAdminLayout>
  );
};

export default TodoStatusManagement;
